import {
  mailboxesCreateMutation,
  mailboxesDestroyMutation,
  mailboxesListOptions,
  mailboxesListQueryKey,
  mailboxesPartialUpdateMutation,
  mailboxesRetrieveOptions,
  ReturnOutlookMailboxReadable
} from '@crm/api'
import { ConfirmModal } from '@crm/components'
import {
  FormModal,
  FormNomenclaturesMailboxes
} from '@crm/features/nomenclatures/components'
import { useTableSorter } from '@crm/hooks/useTableSorter'
import { defaultFilters } from '@crm/utils'
import { DayLabel, TableActions, TableContainer } from '@shared/components'
import {
  useInvalidatePredicateQueries,
  useQueryParams,
  useResetPagination
} from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { showApiErrors } from '@shared/utils'
import { useMutation, useQuery } from '@tanstack/react-query'
import { App, Button } from 'antd'
import { Add, Edit, Login, Trash } from 'iconsax-react'
import { useCallback, useMemo, useState } from 'react'

import type { MenuProps, TableColumnsType } from 'antd'

const formName = 'mailboxes'

export const NomenclaturesMailbox = () => {
  const { t } = useLocales()
  const params = useQueryParams()
  const sorter = useTableSorter()

  const { notification } = App.useApp()
  const { invalidateQueries } = useInvalidatePredicateQueries()
  const { resetPagination } = useResetPagination()
  const [isCreate, setIsCreate] = useState(false)
  const [editItem, setEditItem] = useState<number>()
  const [deleteItem, setDeleteItem] = useState<number>()

  const { data: mailboxes, isLoading } = useQuery(
    mailboxesListOptions({ query: { ...defaultFilters, ...params } })
  )

  const { mutate } = useMutation({
    ...mailboxesDestroyMutation(),
    onError: (e) => showApiErrors(e),
    onSuccess: () => {
      invalidateQueries([mailboxesListQueryKey()])
      resetPagination()
      setDeleteItem(undefined)
      notification.success({
        message: t('success.success'),
        description: t('success.success_data_delete')
      })
    }
  })

  const onDelete = () => {
    if (deleteItem) {
      mutate({ path: { id: deleteItem } })
    }
  }

  const onCancelDelete = () => setDeleteItem(undefined)

  const onCloseModal = () => {
    setEditItem(undefined)
    setIsCreate(false)
  }

  const getActionColumnItem = useCallback(
    (id: number, auth_link: string): MenuProps['items'] => {
      const actions: MenuProps['items'] = [
        {
          label: t('nomenclature.edit'),
          icon: <Edit size={20} />,
          key: 'edit',
          onClick: () => setEditItem(id)
        },
        { type: 'divider' },
        {
          label: t('nomenclature.delete'),
          icon: <Trash size={20} />,
          danger: true,
          key: 'delete',
          onClick: () => setDeleteItem(id)
        }
      ]
      if (auth_link)
        return [
          {
            label: <a href={auth_link}>{t('nomenclature.login')}</a>,
            icon: <Login size={20} />,
            key: 'login'
          },
          ...actions
        ]
      return actions
    },
    [t]
  )

  const columns: TableColumnsType<ReturnOutlookMailboxReadable> = useMemo(
    () => [
      {
        title: t('nomenclature.from_name'),
        dataIndex: 'from_name'
      },
      {
        title: t('nomenclature.from_email'),
        dataIndex: 'from_email'
      },
      {
        title: t('nomenclature.mailbox_type'),
        dataIndex: 'mailbox_type'
      },
      {
        title: t('nomenclature.status'),
        dataIndex: 'status'
      },
      {
        title: t('filter.created_at'),
        dataIndex: 'created_at',
        key: 'created_at',
        sorter: true,
        render: (created_at) => <DayLabel date={created_at} />
      },
      {
        title: t('lead.modified_at'),
        dataIndex: 'modified_at',
        key: 'modified_at',
        sorter: true,
        render: (modified_at) => <DayLabel date={modified_at} />
      },
      {
        width: 50,
        render: ({ id, auth_link }) => (
          <TableActions options={getActionColumnItem(id, auth_link)} />
        )
      }
    ],
    [t, getActionColumnItem]
  )

  return (
    <>
      <TableContainer
        data={mailboxes}
        columns={columns}
        rightChildren={
          <Button
            type="primary"
            onClick={() => setIsCreate(true)}
            icon={<Add size={20} />}
          >
            {t('nomenclature.add_mailbox')}
          </Button>
        }
        loading={isLoading}
        onChange={(_, __, result) => sorter(result)}
        bordered
      />

      <ConfirmModal
        open={!!deleteItem}
        onSuccess={onDelete}
        onCancel={onCancelDelete}
      />

      <FormModal
        id={editItem}
        formName={formName}
        open={!!(editItem || isCreate)}
        getItem={mailboxesRetrieveOptions}
        onCreate={mailboxesCreateMutation}
        onUpdate={mailboxesPartialUpdateMutation}
        onCloseModal={onCloseModal}
      >
        <FormNomenclaturesMailboxes />
      </FormModal>
    </>
  )
}
